import type { Report } from "../../../types/reports";
import {
  EXIT_PLACEMENT_SUBTITLE,
  normalizeReportTypeKey,
  REPORT_TYPE_DISPLAY_LABELS,
} from "../../../utils/reportTypeRules";

export type ReportTypeLaneKey =
  | "trade-analysis"
  | "market-context-performance"
  | "trade-management";

export interface ReportTypeLaneConfig {
  key: ReportTypeLaneKey;
  label: string;
  subtitle: string;
  /** Tailwind classes for lane top accent */
  laneAccentClass: string;
}

export const REPORT_TYPE_LANE_ORDER: ReportTypeLaneConfig[] = [
  {
    key: "trade-analysis",
    label: REPORT_TYPE_DISPLAY_LABELS["trade-analysis"],
    subtitle: "Review closed-trade performance and session insights",
    laneAccentClass: "border-t-2 border-t-[#38BBD1]",
  },
  {
    key: "market-context-performance",
    label: REPORT_TYPE_DISPLAY_LABELS["market-context-performance"],
    subtitle: "See how your edge shifts across market regimes",
    laneAccentClass: "border-t-2 border-t-[#6C4BFF]",
  },
  {
    key: "trade-management",
    label: REPORT_TYPE_DISPLAY_LABELS["trade-management"],
    subtitle: EXIT_PLACEMENT_SUBTITLE,
    laneAccentClass: "border-t-2 border-t-[#F45B0B]",
  },
];

export function resolveReportTypeLaneKey(reportType?: string | null): ReportTypeLaneKey {
  const key = normalizeReportTypeKey(reportType ?? "trade-analysis");
  if (key === "market-context-performance") return "market-context-performance";
  if (key === "trade-management") return "trade-management";
  return "trade-analysis";
}

export function sortReportsNewestFirst(reports: Report[]): Report[] {
  return [...reports].sort((a, b) => {
    const aMs = new Date(a.created_at ?? 0).getTime();
    const bMs = new Date(b.created_at ?? 0).getTime();
    return bMs - aMs;
  });
}

export function groupReportsByTypeLane(
  reports: Report[],
): Record<ReportTypeLaneKey, Report[]> {
  const groups: Record<ReportTypeLaneKey, Report[]> = {
    "trade-analysis": [],
    "market-context-performance": [],
    "trade-management": [],
  };

  for (const report of reports) {
    const lane = resolveReportTypeLaneKey(report.report_type);
    groups[lane].push(report);
  }

  for (const lane of REPORT_TYPE_LANE_ORDER) {
    groups[lane.key] = sortReportsNewestFirst(groups[lane.key]);
  }

  return groups;
}

export function buildGenerateReportPathForLane(laneKey: ReportTypeLaneKey): string {
  return `/reports/new?report_type=${encodeURIComponent(laneKey)}`;
}
